import { IMG } from "@/lib/site";

const R = (title, to, desc) => ({ title, to, desc });

export const pagesC = [
  {
    slug: "/zufahrten-winterdienst-bremen",
    title: "Zufahrten Winterdienst Bremen | Einfahrten & Rampen räumen",
    description:
      "Winterdienst für Zufahrten in Bremen: Einfahrten, Hofzufahrten und Rampen räumen und streuen. Durchgängig befahrbar für Privat und Gewerbe.",
    label: "/ Zufahrten",
    service: "Zufahrten Winterdienst Bremen",
    h1Lines: ["Zufahrten im Winter", "befahrbar halten –", "Winterdienst Bremen."],
    intro:
      "Eine blockierte Zufahrt stoppt alles: das Auto in der Einfahrt, die Anlieferung am Hof, den Rettungsweg zum Gebäude. Wir räumen und streuen Einfahrten, Hofzufahrten und Rampen in Bremen.",
    image: IMG.wideStreet,
    sections: [
      {
        h2: "Zufahrten sind Engstellen – und damit kritisch",
        p: [
          "Eine Zufahrt ist meist schmal, oft mit Gefälle und fast immer die einzige Verbindung zwischen Straße und Grundstück. Fällt sie aus, ist das Objekt nicht erreichbar. Für Privathaushalte heißt das: Das Auto kommt nicht raus. Für Betriebe heißt es: Die Anlieferung fällt aus, Mitarbeitende kommen nicht aufs Gelände, der Rettungsweg ist eingeschränkt.",
          "Besonders problematisch ist der Übergang von der Straße zur Zufahrt. Dort schiebt der kommunale Winterdienst beim Räumen der Fahrbahn oft einen Schneewall quer über die Einfahrt. Diese Wälle sind dicht, schwer und frieren bei Frost schnell zu einer harten Barriere zusammen – mit einer normalen Schneeschaufel ist dagegen kaum anzukommen.",
        ],
      },
      {
        h2: "Das Problem: Gefälle, Schneewälle und zu wenig Platz",
        p: [
          "Bei Zufahrten mit Gefälle genügt eine dünne Eisschicht, damit Fahrzeuge keinen Grip mehr finden – beim Anfahren bergauf wie beim Abbremsen bergab. Dazu kommt die Platzfrage: In schmalen Hofzufahrten gibt es oft keinen Rand, auf den der Schnee geschoben werden kann, ohne die Durchfahrtsbreite zu verlieren.",
          "Ein dritter Punkt ist die Entwässerung. Läuft Tauwasser eine Rampe hinunter und sammelt sich am tiefsten Punkt – häufig direkt vor dem Garagentor oder am Rinnengitter –, entsteht dort nachts die dickste Eisschicht der ganzen Fläche.",
        ],
      },
      {
        h2: "Die Lösung: Durchgang sichern, Gefälle behandeln, Wasser ableiten",
        p: [
          "Wir räumen die Zufahrt auf der vollen nutzbaren Breite, entfernen Schneewälle am Straßenübergang und streuen Gefällestrecken intensiv. Entwässerungspunkte halten wir frei, damit Tauwasser ablaufen kann und sich nicht am Tor sammelt.",
        ],
        bullets: [
          "Einfahrten und Hofzufahrten auf voller Breite räumen",
          "Schneewälle am Übergang zur Straße entfernen",
          "Gefällestrecken und Rampen gezielt streuen",
          "Rinnen und Gullys freihalten",
          "Wendeflächen und Durchfahrten berücksichtigen",
          "Zugang für Rettungs- und Lieferfahrzeuge sichern",
        ],
      },
      {
        h2: "Private und gewerbliche Zufahrten",
        p: [
          "Bei Privatgrundstücken geht es meist um eine Einfahrt mit Garage oder Stellplatz – überschaubare Fläche, dafür hohe Taktung, weil das Auto täglich raus muss. Bei Gewerbeobjekten sind Zufahrten Teil der Betriebslogistik: Lkw-Zufahrten, Rampen zur Laderampe, Tiefgarageneinfahrten und Wendeflächen für Lieferfahrzeuge. Beide Fälle planen wir, beide Fälle haben eigene Zeitfenster.",
        ],
      },
    ],
    steps: [
      { t: "Zufahrt aufnehmen", d: "Breite, Länge, Gefälle, Belag und Entwässerung erfassen." },
      { t: "Schneelager klären", d: "Wohin kann geräumter Schnee, ohne Platz zu verlieren." },
      { t: "Räumen", d: "Volle Breite inklusive Schneewall an der Straße." },
      { t: "Streuen", d: "Gefälle, Toreinfahrt und Rinnenbereich behandeln." },
    ],
    objects: [
      "Private Einfahrten und Stellplätze",
      "Hofzufahrten in Wohnanlagen",
      "Tiefgaragenrampen",
      "Lkw-Zufahrten und Wendeflächen",
      "Rampen zur Laderampe",
      "Zufahrten zu Praxen und Ladengeschäften",
    ],
    local:
      "In vielen Bremer Altbauquartieren führen schmale Hofzufahrten durch Durchfahrten und Tordurchgänge – dort ist jeder Zentimeter Durchfahrtsbreite relevant. In den Gewerbegebieten in Hemelingen, Sebaldsbrück und Walle sind die Zufahrten breiter, stehen aber unter engen Zeitvorgaben durch Anlieferverkehr.",
    faq: [
      { q: "Entfernen Sie auch den Schneewall, den der Straßenwinterdienst hinterlässt?", a: "Ja. Diese verdichteten Wälle an der Einfahrt gehören zu den Standardaufgaben bei der Zufahrtsräumung." },
      { q: "Wird eine Rampe besonders behandelt?", a: "Ja. Gefällestrecken werden intensiver gestreut, weil Fahrzeuge dort anfahren und bremsen." },
      { q: "Was ist, wenn kein Platz für den Schnee vorhanden ist?", a: "Dann legen wir eine Lagerfläche außerhalb der Durchfahrt fest oder organisieren bei größeren Mengen einen Abtransport." },
      { q: "Räumen Sie Zufahrten auch mehrmals täglich?", a: "Bei anhaltendem Schneefall ist Nacharbeit möglich. Umfang und Taktung legen wir im Angebot fest." },
      { q: "Gilt das auch für Tiefgarageneinfahrten?", a: "Ja. Tiefgaragenrampen gehören zu den kritischsten Flächen und werden entsprechend behandelt." },
    ],
    related: [
      R("Garagen & Einfahrten", "/garagen-winterdienst-bremen", "Schneemassen vor dem Tor."),
      R("Parkplatz-Winterdienst", "/parkplatz-winterdienst-bremen", "Parkflächen und Rampen."),
      R("Schneeräumung Bremen", "/schneeraeumung-bremen", "Räumen mit Plan."),
      R("Streudienst Bremen", "/streudienst-bremen", "Gefälle sicher machen."),
      R("Winterdienst Gewerbe", "/winterdienst-bremen-gewerbe-hausverwaltung", "Betriebslogistik im Winter."),
      R("Gehweg-Winterdienst", "/gehweg-winterdienst-bremen", "Einmündung zum Gehweg."),
    ],
  },

  {
    slug: "/garagen-winterdienst-bremen",
    title: "Garagen Winterdienst Bremen | Schnee vor Garage & Einfahrt entfernen",
    description:
      "Winterdienst für Garagen in Bremen: Schneemassen vor Garagentoren, Stellplätzen und Einfahrten entfernen. Garage wieder nutzbar – Angebot auf Anfrage.",
    label: "/ Garagen",
    service: "Garagen Winterdienst Bremen",
    h1Lines: ["Garagen und Einfahrten", "freimachen – Winterdienst", "in Bremen."],
    intro:
      "Vor dem Garagentor sammelt sich der Schnee doppelt: der eigene vom Hof und der verdichtete Wall von der Straße. Wir entfernen Schneemassen vor Garagen, Stellplätzen und Einfahrten in Bremen.",
    image: IMG.snowyCars,
    sections: [
      {
        h2: "Vor dem Tor sammelt sich alles",
        p: [
          "Das Garagentor ist der Punkt, an dem mehrere Schneequellen zusammenkommen. Zum gefallenen Schnee auf der Einfahrt kommt der Wall, den der Straßenwinterdienst quer über die Einfahrt schiebt, und bei Dachgaragen zusätzlich das, was von der Dachkante abrutscht. Das Ergebnis ist eine verdichtete Masse direkt vor dem Tor – oft nasser und schwerer als lockerer Neuschnee.",
          "Dazu kommt ein bauliches Detail: Vor Garagentoren liegt häufig eine Rinne oder ein leichtes Gefälle zum Tor hin. Tauwasser fließt also genau dorthin, wo es stört – und friert dort als Erstes.",
        ],
      },
      {
        h2: "Das Problem: Tor klemmt, Schiene friert, Auto kommt nicht raus",
        p: [
          "Schnee und Eis vor dem Tor haben mehrere unangenehme Folgen. Ein Sektionaltor oder Schwingtor lässt sich nicht öffnen, wenn davor eine verdichtete Schicht liegt. Laufschienen und Bodenschwellen frieren fest. Bei elektrischen Toren kann der Antrieb bei erhöhtem Widerstand abschalten. Und wer das Tor mit Gewalt gegen die Schneekante fährt, riskiert Schäden an Dichtung, Mechanik und Motor.",
          "Praktisch bedeutet das: Das Auto kommt nicht raus, und zwar genau an den Tagen, an denen man es am dringendsten braucht.",
        ],
      },
      {
        h2: "Die Lösung: Tor, Schwelle und Vorfläche konsequent freihalten",
        p: [
          "Wir räumen die Fläche vor dem Tor vollständig frei, lösen verdichtete Wälle, befreien Schwelle und Laufbereich von Eis und streuen die Vorfläche. Dabei achten wir darauf, Dichtungen, Schienen und Torkanten nicht zu beschädigen – an diesen Stellen arbeiten wir von Hand.",
        ],
        bullets: [
          "Fläche vor dem Garagentor vollständig räumen",
          "Verdichtete Schneewälle von der Straße lösen",
          "Torschwelle und Laufbereich von Eis befreien",
          "Rinnen und Abläufe vor dem Tor freihalten",
          "Vorfläche streuen – Anfahren bergauf möglich",
          "Außenstellplätze und Carports mit erfassen",
        ],
      },
      {
        h2: "Garagenhöfe und Stellplatzanlagen",
        p: [
          "Garagenhöfe in Wohnanlagen sind ein eigener Fall: viele Tore nebeneinander, enge Fahrgasse, kein Platz für Schneelagerung. Hier arbeiten wir mit einem festgelegten Lagerpunkt am Rand des Hofs oder organisieren bei größeren Mengen den Abtransport – sonst verliert die Anlage nach zwei Schneefällen die Hälfte ihrer Fahrgasse.",
        ],
      },
    ],
    steps: [
      { t: "Situation aufnehmen", d: "Tortyp, Gefälle, Entwässerung und Platzverhältnisse." },
      { t: "Lagerpunkt festlegen", d: "Wohin der Schnee darf, ohne Tore zu blockieren." },
      { t: "Freimachen", d: "Vorfläche räumen, Wall lösen, Schwelle vom Eis befreien." },
      { t: "Streuen", d: "Vorfläche und Gefälle griffig machen." },
    ],
    objects: [
      "Einzelgaragen am Haus",
      "Garagenhöfe und Garagenzeilen",
      "Carports und Außenstellplätze",
      "Tiefgaragentore und -rampen",
      "Betriebsgaragen und Werkstatttore",
      "Stellplatzanlagen in Wohnquartieren",
    ],
    local:
      "Garagenhöfe sind in Bremer Wohnquartieren weit verbreitet – in der Vahr, in Osterholz, in Huchting, in Gröpelingen und in Bremen-Nord. Diese Anlagen haben im Winter regelmäßig das Platzproblem bei der Schneelagerung, das wir vorab mit der Verwaltung oder Eigentümergemeinschaft klären.",
    faq: [
      { q: "Entfernen Sie auch den harten Wall vor der Einfahrt?", a: "Ja. Verdichtete Schneewälle vom Straßenwinterdienst gehören zum Leistungsumfang." },
      { q: "Wird das Garagentor dabei beschädigt?", a: "Nein. Im Bereich von Schwelle, Schiene und Dichtung arbeiten wir von Hand und bewusst vorsichtig." },
      { q: "Räumen Sie auch Garagenhöfe mit mehreren Toren?", a: "Ja. Hier legen wir vorab einen Schneelagerpunkt fest, damit die Fahrgasse nutzbar bleibt." },
      { q: "Wird der Außenstellplatz mitgeräumt?", a: "Auf Wunsch ja – Stellplätze und Carports können in den Leistungsumfang aufgenommen werden." },
      { q: "Ist das auch als Einzeleinsatz möglich?", a: "Ja, nach Verfügbarkeit, zum Beispiel nach starkem Schneefall." },
    ],
    related: [
      R("Zufahrten", "/zufahrten-winterdienst-bremen", "Einfahrt und Rampe."),
      R("Winterdienst Privat", "/winterdienst-bremen-privat", "Für Haus und Hof."),
      R("Schneeräumung Bremen", "/schneeraeumung-bremen", "Schnee vollständig entfernen."),
      R("Eisbeseitigung Bremen", "/eisbeseitigung-bremen", "Eis an Schwelle und Rinne."),
      R("Parkplatz-Winterdienst", "/parkplatz-winterdienst-bremen", "Stellflächen im Winter."),
      R("Streudienst Bremen", "/streudienst-bremen", "Griffige Vorfläche."),
    ],
  },

  {
    slug: "/treppen-winterdienst-bremen",
    title: "Treppen Winterdienst Bremen | Treppen & Eingänge eisfrei",
    description:
      "Winterdienst für Treppen in Bremen: Außentreppen, Stufen und Eingangsbereiche räumen, streuen und von Eis befreien. Sturzgefahr reduzieren – Angebot auf Anfrage.",
    label: "/ Treppen & Eingänge",
    service: "Treppen Winterdienst Bremen",
    h1Lines: ["Treppen und Eingänge", "eisfrei halten –", "Winterdienst Bremen."],
    intro:
      "Die meisten Winterstürze passieren nicht auf der großen Fläche, sondern an wenigen Stufen. Wir räumen, streuen und enteisen Außentreppen, Podeste und Eingangsbereiche an privaten und gewerblichen Objekten in Bremen.",
    image: IMG.oldTownLane,
    sections: [
      {
        h2: "Wenige Quadratmeter mit dem höchsten Risiko",
        p: [
          "Eine Außentreppe vereint alles, was im Winter gefährlich ist: Höhenunterschied, kleine Trittflächen, häufig glatte Beläge wie Betonwerkstein, Naturstein oder Fliesen, und eine Nutzung durch jede Person, die das Gebäude betritt oder verlässt. Wer hier stürzt, fällt nicht auf die Fläche, sondern über mehrere Stufen.",
          "Hinzu kommt die Bauweise: Stufen werden von allen Seiten angeströmt, kühlen schneller aus als eine geschlossene Fläche und trocknen schlechter. Überdachte Eingänge sind dabei nicht automatisch sicher – sie bekommen kein Tauwasser von oben ab, aber Feuchtigkeit wird hereingetragen und friert in der Zugluft.",
        ],
      },
      {
        h2: "Das Problem: Streugut hält auf Stufen nicht",
        p: [
          "Auf einer Treppe wirkt Streugut schlechter als auf einer Fläche. Sand und Granulat rutschen über die Stufenkante ab oder werden von Schuhen mitgetragen. Auftauendes Streumittel läuft mit dem Schmelzwasser nach unten und sammelt sich in der untersten Stufe – oben bleibt die Stufe glatt.",
          "Das zweite Problem ist die Räumung selbst: Mit großem Gerät ist an Stufen nicht zu arbeiten. Es bleibt Handarbeit, Stufe für Stufe, inklusive Stufenkanten und Treppenwangen, an denen sich Eis besonders hartnäckig hält.",
        ],
      },
      {
        h2: "Die Lösung: Handarbeit, Mehrfachkontrolle, Fokus auf Kanten",
        p: [
          "Wir räumen Treppen von Hand, lösen Eis an Kanten und Wangen und streuen so, dass das Mittel auf der Trittfläche liegen bleibt. Weil Treppen schneller wieder überfrieren als Flächen, gehören sie zu den Bereichen, die wir bei Frostlagen nachkontrollieren.",
        ],
        bullets: [
          "Stufen, Kanten und Podeste von Hand räumen",
          "Eisschichten an Stufenkanten mechanisch lösen",
          "Streugut gezielt auf Trittflächen aufbringen",
          "Handläufe und Zugangsbereiche freihalten",
          "Rampen und barrierefreie Zugänge behandeln",
          "Nachkontrolle bei anhaltendem Frost",
        ],
      },
      {
        h2: "Eingangsbereiche an Gewerbeobjekten",
        p: [
          "Bei Bürogebäuden, Praxen und Ladengeschäften ist der Eingang zugleich Visitenkarte und Risikopunkt. Hier kommt es nicht nur auf Sicherheit an, sondern auch darauf, dass der Bereich sauber aussieht: kein Salzrand auf Naturstein, kein Streusand im Foyer. Wir stimmen das Streumittel deshalb auf den Belag und die Nutzung ab.",
        ],
      },
    ],
    steps: [
      { t: "Treppe aufnehmen", d: "Stufenzahl, Belag, Überdachung, Handlauf, Nutzung." },
      { t: "Mittel wählen", d: "Streugut passend zu Belag und Innenbereich festlegen." },
      { t: "Räumen & enteisen", d: "Handarbeit an Stufen, Kanten und Wangen." },
      { t: "Nachkontrolle", d: "Erneutes Prüfen und Streuen bei Frostlage." },
    ],
    objects: [
      "Haustreppen und Eingangsstufen",
      "Kellerabgänge und Nebeneingänge",
      "Treppen in Wohnanlagen",
      "Eingänge von Bürogebäuden und Praxen",
      "Ladeneingänge und Foyers",
      "Barrierefreie Rampen und Podeste",
    ],
    local:
      "Bremer Altbauten haben häufig erhöhte Hauseingänge mit mehreren Stufen und Natursteinbelägen – typisch in der Östlichen Vorstadt, in Schwachhausen und Findorff. Diese Treppen sind im Winter besonders anspruchsvoll, weil Streusalz dem Naturstein schadet und daher abstumpfende Mittel mit Nachkontrolle die bessere Lösung sind.",
    faq: [
      { q: "Werden Treppen von Hand geräumt?", a: "Ja. Stufen, Kanten und Wangen lassen sich nur in Handarbeit zuverlässig freimachen." },
      { q: "Welches Streumittel nutzen Sie auf Naturstein?", a: "In der Regel abstumpfende Mittel wie Sand oder Granulat, um den Belag nicht zu schädigen – dafür mit Nachkontrolle." },
      { q: "Wird auch ein überdachter Eingang behandelt?", a: "Ja. Überdachte Bereiche können durch eingetragene Feuchte und Zugluft überfrieren und werden mit erfasst." },
      { q: "Gehören Kellerabgänge dazu?", a: "Auf Wunsch ja. Kellerabgänge und Nebeneingänge nehmen wir in den Leistungsumfang auf." },
      { q: "Wie oft wird an Treppen nachgestreut?", a: "So oft es die Frostlage erfordert – Treppen gehören zu den Flächen, die am schnellsten wieder glatt werden." },
    ],
    related: [
      R("Eisbeseitigung Bremen", "/eisbeseitigung-bremen", "Eis an Stufen lösen."),
      R("Gehweg-Winterdienst", "/gehweg-winterdienst-bremen", "Weg zum Eingang."),
      R("Winterdienst Privat", "/winterdienst-bremen-privat", "Haustreppe und Zugang."),
      R("Gewerbeimmobilien", "/winterdienst-gewerbeimmobilien", "Repräsentative Eingänge."),
      R("Streudienst Bremen", "/streudienst-bremen", "Richtiges Streugut."),
      R("Wohnimmobilien", "/winterdienst-immobilien-bremen", "Treppen in Wohnanlagen."),
    ],
  },

  {
    slug: "/winterdienst-immobilien-bremen",
    title: "Winterdienst Immobilien Bremen | Wohnanlagen & Verwaltungen",
    description:
      "Winterdienst für Immobilien in Bremen: Mehrfamilienhäuser, Wohnanlagen und Verwaltungsbestand räumen und streuen. Entlastung für Eigentümer und Hausverwaltungen.",
    label: "/ Immobilien",
    service: "Winterdienst Immobilien Bremen",
    h1Lines: ["Winterdienst für", "Immobilien in Bremen –", "Bestand sicher halten."],
    intro:
      "Mehrfamilienhäuser und Wohnanlagen haben viele kleine Flächen, die im Winter alle gleichzeitig sicher sein müssen. Wir übernehmen den Winterdienst für Immobilieneigentümer, Eigentümergemeinschaften und Hausverwaltungen in Bremen.",
    image: IMG.bremenFacade,
    sections: [
      {
        h2: "Viele Flächen, ein Verantwortlicher",
        p: [
          "Eine Wohnanlage besteht aus mehr als dem Gehweg vor dem Haus. Es gibt Hauseingänge, Treppen, Müllstandorte, Fahrradstellplätze, Wäscheplätze, Zugänge zu Kellern und Nebengebäuden, Stellplätze und Hofzufahrten. Jede dieser Flächen wird genutzt, und jede ist bei Glätte ein Risiko.",
          "Für Eigentümer und Verwalter ist die Lage dabei klar: Die Verkehrssicherungspflicht liegt beim Eigentümer, auch wenn sie an Mieter oder Dienstleister weitergegeben wird. Wer sie an einen Dienstleister überträgt, muss dessen Arbeit zumindest grundsätzlich im Blick behalten. Deshalb spielt die Dokumentation hier eine größere Rolle als bei Privatgrundstücken.",
        ],
      },
      {
        h2: "Das Problem: Eigenleistung durch Mieter funktioniert selten dauerhaft",
        p: [
          "In vielen Bremer Wohnanlagen ist der Winterdienst per Hausordnung auf Mieter übertragen. In der Praxis scheitert das regelmäßig: Mieter sind berufstätig, verreist, gesundheitlich eingeschränkt oder fühlen sich nicht zuständig. Dann bleibt die Fläche liegen – und die Verantwortung landet wieder beim Eigentümer.",
          "Auch der Pflegezustand ist ein Thema: Ohne geeignetes Gerät und Streugut wird zu schmal geräumt, zu spät gestreut, und das Streugut bleibt bis in den Sommer auf den Flächen liegen.",
        ],
      },
      {
        h2: "Die Lösung: Winterdienst als Teil der Objektbetreuung",
        p: [
          "Wir nehmen die Anlage einmal komplett auf, erfassen alle relevanten Flächen und halten sie in einem Räum- und Streuplan fest. Danach läuft der Winterdienst über die Saison, mit festem Ansprechpartner für die Verwaltung und auf Wunsch mit Einsatznachweis. Das entlastet Verwaltung und Mieterschaft gleichzeitig und schafft eine nachvollziehbare Grundlage.",
        ],
        bullets: [
          "Gehwege und Hauszugänge aller Eingänge",
          "Treppen, Podeste und Kellerabgänge",
          "Müllstandorte und Fahrradstellplätze",
          "Hofflächen, Zufahrten und Stellplätze",
          "Garagenhöfe und Außenstellplätze",
          "Streugutaufnahme nach der Saison auf Absprache",
          "Einsatzdokumentation für die Verwaltung",
        ],
      },
      {
        h2: "Für Verwaltungen mit mehreren Objekten",
        p: [
          "Wenn Sie mehrere Objekte in Bremen betreuen, lassen sich Touren bündeln. Das verbessert die Zeitfenster und macht die Abstimmung einfacher, weil alle Objekte über denselben Ansprechpartner laufen. Voraussetzung ist, dass die Objekte im Stadtgebiet oder direkten Umland liegen und sich sinnvoll in eine Tour einordnen lassen.",
        ],
      },
    ],
    steps: [
      { t: "Bestandsaufnahme", d: "Alle Flächen der Anlage erfassen und bewerten." },
      { t: "Plan erstellen", d: "Prioritäten, Zeitfenster und Schneelager festlegen." },
      { t: "Angebot", d: "Objektbezogenes Angebot für die Saison." },
      { t: "Betrieb", d: "Einsätze über den Winter inklusive Nacharbeit." },
      { t: "Nachweis", d: "Dokumentation für Verwaltung und Eigentümer." },
    ],
    objects: [
      "Mehrfamilienhäuser",
      "Wohnanlagen und Quartiere",
      "Eigentümergemeinschaften (WEG)",
      "Seniorenwohnanlagen",
      "Gemischt genutzte Objekte",
      "Verwaltungsbestand mit mehreren Standorten",
    ],
    local:
      "Wir betreuen Wohnimmobilien in den Bremer Wohnquartieren – in der Vahr, in Osterholz, in Hemelingen und Sebaldsbrück, in Findorff und Walle, in Gröpelingen und Huchting sowie in Burglesum, Vegesack und Blumenthal. Für Verwaltungen mit Beständen in verschiedenen Stadtteilen planen wir die Touren entsprechend.",
    faq: [
      { q: "Arbeiten Sie für Hausverwaltungen?", a: "Ja. Wir betreuen Objekte im Auftrag von Verwaltungen, Eigentümern und Eigentümergemeinschaften." },
      { q: "Werden alle Nebenflächen mitgeräumt?", a: "Wir nehmen Müllstandort, Fahrradplätze, Kellerabgänge und Nebeneingänge auf und halten im Angebot fest, was enthalten ist." },
      { q: "Entlastet das die Mieter von der Hausordnung?", a: "Wenn Sie den Winterdienst an uns übertragen, muss das entsprechend in Hausordnung und Kommunikation abgebildet werden. Die praktische Durchführung übernehmen wir." },
      { q: "Erhalten wir einen Nachweis der Einsätze?", a: "Ja, auf Wunsch mit Datum, Uhrzeit und Leistung je Einsatz." },
      { q: "Nehmen Sie das Streugut im Frühjahr auf?", a: "Die Aufnahme abstumpfender Streumittel können wir auf Absprache mit anbieten." },
    ],
    related: [
      R("Gewerbeimmobilien", "/winterdienst-gewerbeimmobilien", "Büro, Handel, Industrie."),
      R("Treppen & Eingänge", "/treppen-winterdienst-bremen", "Zugänge in Wohnanlagen."),
      R("Gehweg-Winterdienst", "/gehweg-winterdienst-bremen", "Anliegerpflichten."),
      R("Garagen & Einfahrten", "/garagen-winterdienst-bremen", "Garagenhöfe räumen."),
      R("Winterdienst Bremen", "/winterdienst-bremen", "Gesamtes Leistungsbild."),
      R("Kontakt", "/kontakt", "Direkt anfragen."),
    ],
  },

  {
    slug: "/winterdienst-24-7-bremen",
    title: "Winterdienst 24/7 Bremen | Bereitschaftsdienst bei Schneefall",
    description:
      "24/7-Bereitschaftsdienst für Winterdienst in Bremen: Einsatz bei Schneefall und Glätte, auch nachts und am Wochenende. Flächen vor Betriebsbeginn geräumt.",
    label: "/ 24/7-Bereitschaft",
    service: "Winterdienst Bereitschaftsdienst Bremen",
    h1Lines: ["24/7-Bereitschaftsdienst", "bei Schneefall –", "Winterdienst Bremen."],
    intro:
      "Schnee und Glätte halten sich nicht an Bürozeiten. Unser Bereitschaftsdienst bedeutet: Wir beobachten die Wetterlage und rücken aus, wenn es nötig ist – auch nachts, früh morgens und am Wochenende.",
    image: IMG.nightStreet,
    sections: [
      {
        h2: "Winterdienst entscheidet sich zwischen 3 und 7 Uhr",
        p: [
          "Der kritische Zeitraum im Winterdienst liegt vor dem Berufsverkehr. Schneefall in der Nacht muss geräumt sein, bevor die ersten Menschen das Haus verlassen. Nachtfrost auf feuchten Flächen muss behandelt sein, bevor der erste Fuß darauf tritt. Wer erst morgens um acht anfängt, arbeitet dem Problem nach.",
          "Bereitschaftsdienst heißt deshalb nicht nur Erreichbarkeit, sondern Vorbereitung: Wetterlage im Blick, Material vorhanden, Geräte einsatzbereit, Touren geplant. Dann ist der eigentliche Einsatz nur noch Ausführung.",
        ],
      },
      {
        h2: "Das Problem: Wetterlagen entstehen kurzfristig",
        p: [
          "In Bremen kippt die Lage oft innerhalb weniger Stunden. Ein milder Regentag endet mit Aufklaren und Frost – und am Morgen ist jede feuchte Fläche glatt. Oder es fällt Schnee ab Mitternacht, der um sechs Uhr bereits festgefahren ist, weil der erste Verkehr darüber gelaufen ist.",
          "Für Betriebe ist der Ausfall teuer: Mitarbeitende kommen nicht aufs Gelände, Anlieferungen stauen sich, Kunden bleiben weg. Für Privathaushalte und Verwaltungen entsteht ein Haftungsrisiko für jede Stunde, in der eine Fläche nicht gesichert ist.",
        ],
      },
      {
        h2: "Die Lösung: Bereitschaft mit klaren Auslösekriterien",
        p: [
          "Wir legen mit Ihnen fest, wann ein Einsatz ausgelöst wird: ab welcher Schneehöhe, bei welcher Wetterlage, zu welchen Zeitfenstern und in welcher Reihenfolge Ihre Flächen abgearbeitet werden. Damit müssen Sie nachts nicht selbst entscheiden, ob heute geräumt werden muss.",
        ],
        bullets: [
          "Beobachtung der Wetterlage über die Saison",
          "Einsatz bei Schneefall – auch nachts und am Wochenende",
          "Glättevorbeugung bei Frostprognose",
          "Flächen vor Berufsverkehr und Schichtbeginn nutzbar",
          "Nachräumen bei anhaltendem Schneefall",
          "Feste Auslösekriterien statt Einzelfallentscheidung",
          "Direkter Ansprechpartner ohne Zentrale",
        ],
      },
      {
        h2: "Was Bereitschaft realistisch bedeutet",
        p: [
          "Wir arbeiten mit einem definierten Einsatzgebiet und planbaren Touren – das ist die Voraussetzung dafür, dass Bereitschaft überhaupt funktioniert. Bei extremen Lagen mit anhaltendem Schneefall werden Flächen in Prioritätsreihenfolge abgearbeitet; alles gleichzeitig ist physikalisch nicht möglich. Diese Reihenfolge legen wir vorher gemeinsam fest, damit im Ernstfall klar ist, was zuerst frei ist.",
          "Wir machen dazu keine pauschalen Versprechen über Minutenzeiten. Was wir zusagen, ist eine abgestimmte Priorität und ein Ansprechpartner, der erreichbar ist.",
        ],
      },
    ],
    steps: [
      { t: "Kriterien festlegen", d: "Ab wann wird ausgelöst – Schneehöhe, Frost, Zeitfenster." },
      { t: "Prioritäten klären", d: "Welche Fläche zuerst, welche kann später." },
      { t: "Saison aktiv", d: "Wetterbeobachtung und Einsatzbereitschaft." },
      { t: "Einsatz", d: "Räumen und Streuen in festgelegter Reihenfolge." },
      { t: "Nacharbeit", d: "Zweiter Durchgang bei anhaltendem Schneefall." },
    ],
    objects: [
      "Betriebe mit Früh- und Schichtbetrieb",
      "Handelsstandorte mit festen Öffnungszeiten",
      "Praxen, Kliniken und Pflegeeinrichtungen",
      "Logistik- und Lieferstandorte",
      "Wohnanlagen mit hoher Frequenz",
      "Privatobjekte mit frühem Bedarf",
    ],
    local:
      "Unser Firmensitz an der Flughafenallee auf der Überseeinsel; der Fuhrpark startet aus Bremen-Hemelingen ist für nächtliche Einsätze ein Vorteil: Der Osten Bremens ist schnell erreicht, das übrige Stadtgebiet über die Ringstraßen gut angebunden. Die Touren planen wir so, dass Nachtschneefall bis zum Berufsverkehr abgearbeitet ist.",
    faq: [
      { q: "Sind Sie wirklich rund um die Uhr erreichbar?", a: "Wir halten über die Wintersaison Bereitschaft und sind für betreute Objekte erreichbar – auch nachts und am Wochenende. Einsätze werden nach vereinbarten Kriterien und Prioritäten ausgeführt." },
      { q: "Garantieren Sie eine bestimmte Einsatzzeit?", a: "Wir nennen keine pauschalen Minutenzeiten. Stattdessen legen wir Zeitfenster und Prioritäten fest, die zu Ihrem Objekt passen und realistisch eingehalten werden können." },
      { q: "Wer entscheidet, ob geräumt wird?", a: "Wir – auf Grundlage der vorab festgelegten Auslösekriterien. Sie müssen nachts nicht selbst beurteilen, ob ein Einsatz nötig ist." },
      { q: "Was passiert bei extremem Dauerschneefall?", a: "Dann arbeiten wir Flächen in der vereinbarten Prioritätsreihenfolge ab und räumen nach. Die Reihenfolge wird vorab definiert." },
      { q: "Gilt die Bereitschaft auch für Einzeleinsätze?", a: "Bereitschaft setzt eine Saisonvereinbarung voraus. Einzelne Einsätze sind nach Verfügbarkeit möglich, aber ohne Bereitschaftszusage." },
    ],
    related: [
      R("Winterdienst Gewerbe", "/winterdienst-bremen-gewerbe-hausverwaltung", "Betriebsbereitschaft im Winter."),
      R("Parkplatz-Winterdienst", "/parkplatz-winterdienst-bremen", "Vor Schichtbeginn geräumt."),
      R("Streudienst Bremen", "/streudienst-bremen", "Glättevorbeugung bei Nachtfrost."),
      R("Schneeräumung Bremen", "/schneeraeumung-bremen", "Räumen vor dem Verkehr."),
      R("Gewerbeimmobilien", "/winterdienst-gewerbeimmobilien", "Objektbetreuung."),
      R("Kontakt", "/kontakt", "Bereitschaft anfragen."),
    ],
  },
];
